import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { handleTokenAndPostRequest } from "../utils/utils";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import UploadContactsModal from "../components/UploadContactsModal";
import {
  FaCloudUploadAlt, FaCog, FaDownload, FaExternalLinkAlt,
  FaHome, FaShareAlt, FaQrcode, FaNewspaper, FaChartArea,
  FaCheckCircle, FaRegCircle, FaRocket, FaChevronDown, FaChevronUp,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { trackAgentEvent } from "../utils/agentEventTracking";

export default function Onboarding() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selfChecks, setSelfChecks] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const saveTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    async function fetchChecklist() {
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/onboarding-checklist`, {}
        );
        setData(res.data);
        // Seed self-checks from server-side agent_settings.onboarding
        const saved = res.data?.agent_settings?.onboarding || {};
        setSelfChecks(saved);
      } catch (err) {
        console.error("Failed to load onboarding checklist", err);
      } finally {
        setLoading(false);
      }
    }
    fetchChecklist();
  }, []);

  // Persist self-checks to the server (debounced)
  const persistSelfChecks = useCallback((checks) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        { agent_settings: { ...(data?.agent_settings || {}), onboarding: checks } }
      ).catch(() => {});
    }, 600);
  }, [data?.agent_settings]);

  const toggleSelfCheck = useCallback((key) => {
    setSelfChecks(prev => {
      const next = { ...prev, [key]: !prev[key] };
      trackAgentEvent({
        eventName: `onboarding.getting_started.${key}.${next[key] ? 'check' : 'uncheck'}`,
        metadata: { trigger_context: { checklist_item: key } },
      });
      persistSelfChecks(next);
      return next;
    });
  }, [persistSelfChecks]);

  const slug = data?.landing_page_slug || "";
  const agentSearchUrl = slug ? `https://www.homeprice.site/property-search?agent=${slug}` : "";
  const agentLiveUrl = slug ? `https://www.homeprice.site/property-search-live?agent=${slug}` : "";

  const checklist = [
    {
      id: "upload_contacts",
      label: "Upload Your Contacts",
      description: "If you've already shared CRM access or uploaded a contacts spreadsheet, you're good to go — tick this one off. Otherwise, click \"Upload Contacts\" in the sidebar to get started.",
      icon: <FaCloudUploadAlt />,
      done: selfChecks.upload_contacts,
      selfCheckable: true,
      action: { label: "Upload Contacts", onClick: () => setShowUploadModal(true) },
    },
    {
      id: "check_settings",
      label: "Check Your Settings & Branding",
      description: "Make sure your logo, profile photo, brand colour, agency name, and contact details are all filled in. This ensures your property reports, articles, and QR flyers look professional with your branding.",
      icon: <FaCog />,
      done: data?.settings_complete,
      autoChecked: true,
      action: { label: "Open Settings", onClick: () => navigate("/settings") },
    },
    {
      id: "update_target_suburbs",
      label: "Update Your List of Target Suburbs",
      description: "Go to Settings and add the suburbs you operate in. This helps us tailor your market update emails and content to the areas most relevant to your business.",
      icon: <FaMapMarkerAlt />,
      done: data?.has_target_suburbs,
      autoChecked: true,
      action: { label: "Open Settings", onClick: () => navigate("/settings") },
    },
    {
      id: "download_report",
      label: "Download a Property Report With Your Branding",
      description: "Open your branded property search page, search for any address, and send yourself the email report. Check that your logo, colours, and contact details appear correctly, and that all the links work and go to the right place. This is exactly what your prospects will receive.",
      icon: <FaDownload />,
      done: data?.has_property_report || selfChecks.download_report,
      autoChecked: data?.has_property_report,
      selfCheckable: !data?.has_property_report,
      action: agentSearchUrl
        ? { label: "Open Your Property Search Page", href: agentSearchUrl, external: true }
        : null,
    },
    {
      id: "view_live_report",
      label: "View a Live Property Report",
      description: "Open your live property report page to see exactly what your prospects experience when they view a report. Check that your branding looks right and everything loads correctly.",
      icon: <FaExternalLinkAlt />,
      done: selfChecks.view_live_report,
      selfCheckable: true,
      action: agentLiveUrl
        ? { label: "Open Live Property Report", href: agentLiveUrl, external: true }
        : null,
    },
    {
      id: "create_appraisal",
      label: "Create Your First Appraisal",
      description: "Go to the Appraisals page and click \"New Appraisal\". Search for a property address, set bedrooms/bathrooms/parking, adjust the price range, and add comparable sales. You can also run the AI Comparable Analysis to auto-populate sales data.",
      icon: <FaHome />,
      done: data?.has_appraisals,
      autoChecked: true,
      action: { label: "Go to Appraisals", onClick: () => navigate("/leads-property-appraisals") },
      grouped: true,
    },
    {
      id: "publish_appraisal",
      label: "Publish Your First Appraisal",
      description: "Once your appraisal is ready, hit \"Publish\" to generate a shareable link. You can then send it directly to your prospect via email from the platform, or copy the link to share via SMS or other channels.",
      icon: <FaShareAlt />,
      done: data?.has_published_appraisal,
      autoChecked: true,
      action: { label: "Go to Appraisals", onClick: () => navigate("/leads-property-appraisals") },
      groupedWith: "create_appraisal",
    },
    {
      id: "download_qr",
      label: "Download & Print Your QR Code or Premade Flyer",
      description: "Go to the QR Code page to download your personalised QR code, or generate a ready-to-print A5 flyer with your branding. Print it and place it in letterboxes, open homes, or shopfronts to drive prospects to your branded property search page.",
      icon: <FaQrcode />,
      done: selfChecks.download_qr,
      selfCheckable: true,
      action: { label: "Go to QR Code Page", onClick: () => navigate("/campaign-qr-code") },
    },
    {
      id: "generate_article",
      label: "Generate & Share Your First Article",
      description: "Go to AI Articles and click \"Generate Article\". Select suburbs, optionally enter a topic or let the AI choose, then generate. Once created, review the content, make any edits, publish it, and use the social blurb generator to create a post for Facebook or Instagram.",
      icon: <FaNewspaper />,
      done: data?.has_articles,
      autoChecked: true,
      action: { label: "Go to AI Articles", onClick: () => navigate("/ai-news-articles") },
    },
    {
      id: "generate_infographic",
      label: "Generate & Share Your AI Infographic",
      description: "Go to AI Infographics, pick your suburbs, choose sales or rental data, select a size (square for Instagram, landscape for Facebook), and hit Generate. Download the PNG and share it on your social media to position yourself as the local market expert.",
      icon: <FaChartArea />,
      done: data?.has_infographics,
      autoChecked: true,
      action: { label: "Go to AI Infographics", onClick: () => navigate("/campaign-digital-ads") },
    },
  ];

  const completedCount = checklist.filter(i => i.done).length;
  const totalCount = checklist.length;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div style={{
        flex: 1,
        overflowY: "auto",
        paddingTop: isMobile ? 56 : 0,
        background: "#f6f8fa",
      }}>
        {/* Header skeleton */}
        <div style={{ background: "#fff", padding: isMobile ? "16px 16px 20px" : "24px 32px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Skeleton circle width={28} height={28} />
            <Skeleton width={200} height={28} />
          </div>
          <Skeleton width="60%" height={14} style={{ marginBottom: 16 }} />
          <Skeleton width={120} height={14} style={{ marginBottom: 8 }} />
          <Skeleton height={10} borderRadius={8} />
        </div>
        {/* Checklist skeleton */}
        <div style={{ padding: isMobile ? "20px 16px 40px" : "28px 32px 60px", display: "flex", flexDirection: "column", gap: 12 }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 12, padding: isMobile ? 14 : 16,
              border: "1px solid #e5e7eb",
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <Skeleton circle width={32} height={32} />
              <div style={{ flex: 1 }}>
                <Skeleton width="50%" height={15} />
                <Skeleton width="80%" height={12} style={{ marginTop: 6 }} />
              </div>
              <Skeleton width={80} height={32} borderRadius={8} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      flex: 1,
      overflowY: "auto",
      paddingTop: isMobile ? 56 : 0,
      background: "#f6f8fa",
    }}>
      {/* Header */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #e5e7eb",
        padding: isMobile ? "16px 16px 20px" : "24px 32px 28px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
          <FaRocket style={{ color: "#10b981", fontSize: isMobile ? 22 : 28 }} />
          <h1 style={{
            fontSize: isMobile ? 22 : 32,
            fontWeight: 700, color: "#111827", margin: 0,
          }}>
            Getting Started
          </h1>
        </div>
        <p style={{
          color: "#6b7280", fontSize: isMobile ? 13 : 15,
          margin: "8px 0 16px", maxWidth: 600, lineHeight: 1.5,
        }}>
          Complete these steps to make the most of the Vendorleads platform. Items are automatically checked when we detect you've completed them.
        </p>

        {/* Progress */}
        <div style={{ maxWidth: 480 }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "baseline",
            marginBottom: 6,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
              {completedCount} of {totalCount} completed
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: pct === 100 ? "#10b981" : "#6b7280" }}>
              {pct}%
            </span>
          </div>
          <div style={{
            height: 8, borderRadius: 4, background: "#e5e7eb", overflow: "hidden",
          }}>
            <div style={{
              height: "100%", borderRadius: 4,
              width: `${pct}%`,
              background: pct === 100
                ? "linear-gradient(90deg, #10b981, #059669)"
                : "linear-gradient(90deg, #0ea5e9, #0d9488)",
              transition: "width 0.4s ease",
            }} />
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div style={{
        padding: isMobile ? "20px 16px 40px" : "28px 32px 60px",
        maxWidth: 720,
      }}>
        {checklist.map((item, idx) => {
          const isExpanded = expandedItem === item.id;
          const isGroupStart = item.grouped;
          const isGroupChild = item.groupedWith;
          return (
            <div
              key={item.id}
              style={{
                marginTop: isGroupChild ? 0 : (idx === 0 ? 0 : 12),
                borderRadius: isGroupChild ? "0 0 12px 12px"
                  : isGroupStart ? "12px 12px 0 0" : 12,
                border: "1px solid " + (item.done ? "#d1fae5" : "#e5e7eb"),
                borderTop: isGroupChild ? "1px dashed #e5e7eb" : undefined,
                background: item.done ? "#f0fdf4" : "#fff",
                overflow: "hidden",
                transition: "background 0.2s, border-color 0.2s",
              }}
            >
              {/* Row */}
              <div
                data-track-click="true"
                data-track-card="true"
                  data-track-event="false"
                data-event-tab="getting_started"
                data-event-field={item.id}
                data-event-action={isExpanded ? "collapse" : "expand"}
                onClick={() => {
                  trackAgentEvent({
                    eventName: `onboarding.getting_started.${item.id}.${isExpanded ? 'collapse' : 'expand'}`,
                    metadata: { trigger_context: { checklist_item: item.id } },
                  });
                  setExpandedItem(isExpanded ? null : item.id);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: isMobile ? "14px 14px" : "16px 20px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {/* Check icon */}
                <div
                  onClick={(e) => {
                    if (item.selfCheckable) { e.stopPropagation(); toggleSelfCheck(item.id); }
                  }}
                  style={{
                    flexShrink: 0, display: "flex", alignItems: "center",
                    cursor: item.selfCheckable ? "pointer" : "default",
                  }}
                  title={item.selfCheckable ? "Mark as done" : (item.autoChecked ? "Auto-detected" : "")}
                >
                  {item.done
                    ? <FaCheckCircle style={{ fontSize: 22, color: "#10b981" }} />
                    : <FaRegCircle style={{ fontSize: 22, color: item.selfCheckable ? "#9ca3af" : "#d1d5db" }} />
                  }
                </div>

                {/* Icon */}
                <div style={{
                  flexShrink: 0, width: 36, height: 36, borderRadius: 8,
                  background: item.done ? "#d1fae5" : "#f3f4f6",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: item.done ? "#059669" : "#6b7280",
                  fontSize: 16,
                }}>
                  {item.icon}
                </div>

                {/* Label */}
                <span style={{
                  flex: 1, fontSize: isMobile ? 14 : 15, fontWeight: 500,
                  color: item.done ? "#059669" : "#111827",
                  textDecoration: item.done ? "line-through" : "none",
                }}>
                  {item.label}
                </span>

                {/* Auto badge */}
                {item.autoChecked && item.done && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: "#059669",
                    background: "#d1fae5", padding: "2px 8px", borderRadius: 10,
                    flexShrink: 0, display: isMobile ? "none" : "inline",
                  }}>
                    AUTO
                  </span>
                )}

                {/* Expand arrow */}
                <div style={{ flexShrink: 0, color: "#9ca3af", fontSize: 12 }}>
                  {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{
                  padding: isMobile ? "0 14px 16px 62px" : "0 20px 20px 68px",
                  borderTop: "1px solid " + (item.done ? "#d1fae5" : "#f3f4f6"),
                  paddingTop: 14,
                }}>
                  <p style={{
                    fontSize: 13, color: "#4b5563", lineHeight: 1.6,
                    margin: "0 0 14px",
                  }}>
                    {item.description}
                  </p>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    {item.action && (
                      item.action.external ? (
                        <a
                          href={item.action.href}
                          target="_blank"
                          rel="noopener noreferrer"
                            data-track-event="false"
                          onClick={() => {
                            trackAgentEvent({
                              eventName: `onboarding.getting_started.${item.id}.open_link`,
                              metadata: { trigger_context: { checklist_item: item.id, href: item.action.href } },
                            });
                          }}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "8px 16px", borderRadius: 8,
                            background: "#0d9488", color: "#fff",
                            fontSize: 13, fontWeight: 600, textDecoration: "none",
                            border: "none", cursor: "pointer",
                          }}
                        >
                          {item.action.label}
                          <FaExternalLinkAlt style={{ fontSize: 11 }} />
                        </a>
                      ) : (
                        <button
                            data-track-event="false"
                          onClick={() => {
                            trackAgentEvent({
                              eventName: `onboarding.getting_started.${item.id}.click_action`,
                              metadata: { trigger_context: { checklist_item: item.id, action_label: item.action.label } },
                            });
                            item.action.onClick();
                          }}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "8px 16px", borderRadius: 8,
                            background: "#0d9488", color: "#fff",
                            fontSize: 13, fontWeight: 600,
                            border: "none", cursor: "pointer",
                          }}
                        >
                          {item.action.label}
                        </button>
                      )
                    )}

                    {item.selfCheckable && !item.done && (
                      <button
                        data-track-event="false"
                        onClick={() => toggleSelfCheck(item.id)}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          padding: "8px 16px", borderRadius: 8,
                          background: "#fff", color: "#374151",
                          fontSize: 13, fontWeight: 500,
                          border: "1px solid #d1d5db", cursor: "pointer",
                        }}
                      >
                        <FaCheckCircle style={{ fontSize: 13, color: "#9ca3af" }} />
                        Mark as Done
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Completion message */}
        {pct === 100 && (
          <div style={{
            marginTop: 24, padding: 24, borderRadius: 12,
            background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
            border: "1px solid #a7f3d0",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#065f46", marginBottom: 4 }}>
              You're all set!
            </div>
            <p style={{ fontSize: 14, color: "#047857", margin: 0 }}>
              You've completed all the onboarding steps. Head to your{" "}
              <span
                onClick={() => {
                  trackAgentEvent({
                    eventName: "onboarding.getting_started.dashboard_link.navigate",
                    metadata: { trigger_context: { from: "onboarding_completion" } },
                  });
                  navigate("/dashboard");
                }}
                style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
              >
                Dashboard
              </span>{" "}
              to monitor your leads and activity.
            </p>
          </div>
        )}
      </div>
      <UploadContactsModal show={showUploadModal} onClose={() => setShowUploadModal(false)} />
    </div>
  );
}
